import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  Autocomplete,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  dialogPaperProps,
  greyButtonStyle,
  missingModalField,
  modalActions,
  primaryButtonStyle,
  secondaryButtonStyle,
} from "./styles";
import CatalogResolveResult from "./CatalogResolveResult";
import { useSnackbar } from "../../../../Components/SnackBar";
import { apiPostRequest } from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import PointerEvents from "../../../../Utils/pointer";
import { useTranslation } from "react-i18next";
import { catalogName } from "../../../../Utils/catalogName";

// Same rule as the API: above 0, at most two decimals.
const PRICE_PATTERN = /^\d+(\.\d{1,2})?$/;

const MissingServiceModal = ({
  showMissingModal,
  setShowMissingModal,
  categories = [],
  searchTerm = "",
  subcategoryId = "",
  subcategoryName = "",
  // (pricelistId, supplierHasPricelist) => void: opens the pricelist editor
  // for that pricelist (the same editor the search opens, which is also how a
  // supplier joins a pricelist they don't have yet).
  onOpenPricelist,
}) => {
  const { t, i18n } = useTranslation([
    "missing-service-modal",
    "categories",
    "services",
    "sku",
  ]);
  const snackbar = useSnackbar();
  const [serviceName, setServiceName] = useState(searchTerm);
  const [importantNote, setImportantNote] = useState("");
  const [categoryInputValue, setCategoryInputValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [price, setPrice] = useState("");
  // "form" = typing the request; "result" = showing the catalog check answer.
  const [phase, setPhase] = useState("form");
  const [view, setView] = useState(null);
  const [runId, setRunId] = useState(null);
  const [checking, setChecking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);
  // null | "failed" | "rate_limited"
  const [createError, setCreateError] = useState(null);
  const [checkError, setCheckError] = useState(null);
  // Double-click guard: state updates are async, a ref is not.
  const inFlight = useRef(false);

  const trimmedName = serviceName.trim();
  const priceIsValid =
    price === "" || (PRICE_PATTERN.test(price) && Number(price) > 0);
  const canCheck = trimmedName.length >= 2 && trimmedName.length <= 200;
  const busy = checking || adding || sending;

  useEffect(() => {
    if (subcategoryId && subcategoryName && categories.length) {
      const found = categories.find(
        (c) =>
          String(c.id) === String(subcategoryId) &&
          c.name.toLowerCase() === subcategoryName.toLocaleLowerCase()
      );
      if (found) setSelectedCategory(found);
    }
  }, [subcategoryId, categories, subcategoryName]);

  const getFinalCategory = () => {
    const trimmedInput = categoryInputValue?.trim()?.toLowerCase();
    const matchedCategory = categories.find(
      (cat) => cat.name.toLowerCase() === trimmedInput
    );

    return selectedCategory?.id
      ? selectedCategory
      : matchedCategory
        ? matchedCategory
        : null;
  };

  // The feedback path (unchanged behaviour): files the request for the
  // Keilot team. Also the fallback when the catalog check cannot help.
  const sendFeedback = async () => {
    if (!serviceName) return;

    const finalCategory = getFinalCategory();

    const isCustomCategory = !finalCategory;

    const additionalInfo = importantNote
      ? isCustomCategory && categoryInputValue
        ? `${importantNote} - ${t("new_category")}${categoryInputValue}`
        : importantNote
      : isCustomCategory && categoryInputValue
        ? `${t("new_category")}${categoryInputValue}`
        : null;

    const body = {
      service_name: serviceName,
      additional_info: additionalInfo,
      category_id: finalCategory ? Number(finalCategory.id) : null,
      category_name: finalCategory ? finalCategory.name : categoryInputValue,
    };

    try {
      setSending(true);
      PointerEvents.disable();
      const response = await apiPostRequest(
        endpoints.submitMissingServiceRequest,
        body
      );
      if (response.status === 200) {
        snackbar(t("missing_service_submitted_success"), "success");
        handleModalClose();
      }
    } catch (error) {
      snackbar(t("missing_service_submitted_error"), "error");
    } finally {
      PointerEvents.enable();
      setSending(false);
    }
  };

  const handleSubmit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await sendFeedback();
    } finally {
      inFlight.current = false;
    }
  };

  // Calls the catalog skill. Returns { result } on 200, otherwise
  // { error: "rate_limited" | "failed" }. Never files feedback by itself:
  // the supplier decides that with "Send to the Keilot team instead".
  const runResolve = async () => {
    // A pricelist hint only when opened from the pricelist editor, where the
    // pricelist (subcategory) id is known. The category autocomplete mixes
    // category, service and variant ids, so its choice is never sent.
    const pricelistId = Number(subcategoryId) || null;
    const body = {
      request: trimmedName,
      ...(price !== "" ? { price: Number(price) } : {}),
      ...(pricelistId ? { pricelist_id: pricelistId } : {}),
    };
    try {
      const response = await apiPostRequest(endpoints.catalogResolve, body);
      if (response.status === 200 && response.data) {
        return { result: response.data };
      }
      return { error: "failed" };
    } catch (error) {
      return {
        error: error?.response?.status === 429 ? "rate_limited" : "failed",
      };
    }
  };

  const showResolveOutcome = ({ result, error }, extra = {}) => {
    if (error) {
      setCheckError(error);
      setPhase("form");
      setView(null);
      setRunId(null);
      return;
    }
    const usable =
      (result.decision === "match" && result.matches?.length) ||
      (result.decision === "similar" &&
        (result.matches?.length || result.proposal)) ||
      (result.decision === "create" && result.proposal);
    setCheckError(null);
    setRunId(usable ? result.run_id : null);
    setView(
      usable
        ? { kind: "result", result, ...extra }
        : // out_of_scope / not_a_service (or an empty answer): explain and
          // leave the feedback path to the supplier.
          { kind: "unsupported", decision: result.decision }
    );
    setPhase("result");
  };

  // Step 1: ask the catalog skill whether the service exists already.
  const handleCheck = async () => {
    if (inFlight.current || !canCheck || !priceIsValid) return;
    inFlight.current = true;
    setChecking(true);
    setCreateError(null);
    setCheckError(null);
    try {
      showResolveOutcome(await runResolve());
    } finally {
      setChecking(false);
      inFlight.current = false;
    }
  };

  const handleOpenPricelist = (pricelistId, supplierHasPricelist) => {
    handleModalClose();
    if (onOpenPricelist && pricelistId) {
      onOpenPricelist(pricelistId, supplierHasPricelist);
    }
  };

  // Step 2: add the proposed item to the shared catalog. The price is never
  // saved here; the supplier prices the new item in the pricelist editor.
  const handleAdd = async () => {
    if (inFlight.current || !runId) return;
    inFlight.current = true;
    setAdding(true);
    setCreateError(null);
    const proposal = view?.result?.proposal;
    try {
      const response = await apiPostRequest(endpoints.catalogCreate, {
        run_id: runId,
      });
      if (response.status === 200) {
        const created = response.data;
        if (created.supplier_has_pricelist && onOpenPricelist) {
          snackbar(t("catalog.created_title"), "success");
          handleOpenPricelist(created.pricelist_id, true);
        } else {
          setView({ kind: "created", created, proposal });
        }
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        setView({ kind: "duplicate", match: error.response.data?.match });
      } else if (status === 429) {
        setCreateError("rate_limited");
      } else if (status === 400 && !view?.rechecked) {
        // The check expired (24 h) or is no longer valid: check again once
        // and show the fresh answer, so the supplier can add from it.
        showResolveOutcome(await runResolve(), { rechecked: true });
      } else {
        setCreateError("failed");
      }
    } finally {
      setAdding(false);
      inFlight.current = false;
    }
  };

  const handleEditRequest = () => {
    setPhase("form");
    setView(null);
    setRunId(null);
    setCreateError(null);
  };

  const handleModalClose = () => {
    setShowMissingModal(false);
    setServiceName("");
    setImportantNote("");
    setSelectedCategory(null);
    setPrice("");
    setPhase("form");
    setView(null);
    setRunId(null);
    setCreateError(null);
    setCheckError(null);
  };

  return (
    <Dialog
      open={showMissingModal}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleModalClose();
      }}
      disableEscapeKeyDown
      PaperProps={{ sx: dialogPaperProps }}
      fullWidth
      maxWidth="sm"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          justifyContent: "space-between",
          borderBottom: "1px solid #EAEAEA",
        }}
      >
        <DialogTitle
          sx={{
            ml: "-17px",
            fontWeight: 600,
            fontSize: "18px",
            color: "#2F2B3DE5",
            p: 0,
            pl: 2,
            py: 2,
          }}
        >
          {t("tell_what_missing")}
        </DialogTitle>
        <IconButton onClick={handleModalClose} aria-label={t("close")}>
          <CloseIcon sx={{ fontSize: "19px" }} />
        </IconButton>
      </Box>

      {/* Body */}
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 3 }}
      >
        {phase === "result" ? (
          <CatalogResolveResult
            {...{
              view,
              requestText: trimmedName,
              price,
              adding,
              busy,
              createError,
              onOpenPricelist: handleOpenPricelist,
              onAdd: handleAdd,
            }}
          />
        ) : (
          <>
            {checkError ? (
              <Alert
                severity={checkError === "rate_limited" ? "warning" : "error"}
                action={
                  checkError === "failed" ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleCheck}
                      disabled={busy}
                      sx={{ textTransform: "none" }}
                    >
                      {t("try_again")}
                    </Button>
                  ) : null
                }
              >
                {checkError === "rate_limited"
                  ? t("rate_limited")
                  : t("check_error")}
              </Alert>
            ) : null}
            <Box>
              <Typography
                component="label"
                htmlFor="missing-service-name"
                display="block"
                fontSize="15px"
                mb={1}
                color="#2F2B3DE5"
                fontWeight={500}
              >
                {t("what_service_or_category_missing")} *
              </Typography>
              <TextField
                id="missing-service-name"
                fullWidth
                required
                autoFocus
                placeholder={t("missing_service_placeholder")}
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCheck();
                  }
                }}
                inputProps={{ maxLength: 200 }}
                disabled={busy}
                sx={missingModalField}
              />
            </Box>

            <Box>
              <Typography
                component="label"
                htmlFor="missing-service-price"
                display="block"
                fontSize="15px"
                mb={1}
                color="#2F2B3DE5"
                fontWeight={500}
              >
                {t("price_label")}{" "}
                <Typography component="span" fontSize="13px" fontWeight={400}>
                  ({t("optional")})
                </Typography>
              </Typography>
              <TextField
                id="missing-service-price"
                fullWidth
                size="small"
                placeholder={t("price_placeholder")}
                value={price}
                onChange={(e) => setPrice(e.target.value.trim())}
                inputProps={{ inputMode: "decimal", dir: "ltr" }}
                error={!priceIsValid}
                helperText={!priceIsValid ? t("price_invalid") : " "}
                disabled={busy}
                sx={{ ...missingModalField, maxWidth: "200px" }}
              />
            </Box>

            <Box>
              <Typography fontSize="15px" mb={1} fontWeight={500}>
                {t("important_information")}{" "}
                <Typography component="span" fontSize="13px" fontWeight={400}>
                  ({t("optional")})
                </Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={4}
                placeholder={t("additional_details")}
                value={importantNote}
                onChange={(e) => setImportantNote(e.target.value)}
                sx={missingModalField}
              />
            </Box>

            <Box>
              <Typography
                color="#2F2B3DE5"
                fontSize="15px"
                mb={1}
                fontWeight={500}
              >
                {t("where_should_located")}{" "}
                <Typography component="span" fontSize="13px" fontWeight={400}>
                  ({t("optional")})
                </Typography>
              </Typography>

              <Autocomplete
                freeSolo
                options={categories}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : catalogName(t, option.name, {
                        ns: ["categories", "services", "sku"],
                        nameHe: option.name_he,
                        language: i18n.language,
                      })
                }
                value={selectedCategory || null}
                inputValue={categoryInputValue}
                onInputChange={(event, newInputValue) => {
                  setCategoryInputValue(newInputValue);
                  if (
                    selectedCategory &&
                    newInputValue !== selectedCategory.name
                  ) {
                    setSelectedCategory(null);
                  }
                }}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    setSelectedCategory({ name: newValue });
                  } else if (newValue && typeof newValue === "object") {
                    setSelectedCategory(newValue);
                  } else {
                    setSelectedCategory(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("select_or_type_category")}
                    size="small"
                    sx={missingModalField}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id &&
                  option?.name.toLowerCase() === value?.name.toLowerCase()
                }
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ ...modalActions, flexWrap: "wrap", gap: 1 }}>
        {view?.kind !== "created" && (
          <Button
            onClick={phase === "result" ? handleEditRequest : handleModalClose}
            variant="contained"
            disabled={busy}
            sx={greyButtonStyle}
          >
            {phase === "result" ? t("edit_request") : t("back")}
          </Button>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-end",
            marginInlineStart: "auto !important",
          }}
        >
          {view?.kind !== "created" && (
            <Button
              onClick={handleSubmit}
              variant="outlined"
              disabled={!serviceName || busy}
              startIcon={
                sending ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={secondaryButtonStyle}
            >
              {t("send_to_team_instead")}
            </Button>
          )}
          {phase === "form" && (
            <Button
              onClick={handleCheck}
              variant="contained"
              disabled={!canCheck || !priceIsValid || busy}
              startIcon={
                checking ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={primaryButtonStyle}
            >
              {checking ? t("checking") : t("check")}
            </Button>
          )}
          {view?.kind === "created" && (
            <Button
              onClick={handleModalClose}
              variant="outlined"
              sx={secondaryButtonStyle}
            >
              {t("close")}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MissingServiceModal;
